import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import AuthLogin from './components/AuthLogin';
import TeacherDashboard from './components/teacher/TeacherDashboard';
import YoxlamaPage from './components/teacher/YoxlamaPage';
import BuraxilisYoxlamaPage from './components/teacher/BuraxilisYoxlamaPage';
import AdminDashboard from './components/admin/AdminDashboard';
import TeacherManagement from './components/admin/TeacherManagement';
import AssignmentManagement from './components/admin/AssignmentManagement';
import StatisticsPage from './components/admin/StatisticsPage';
import StudentResultsPage from './components/admin/StudentResultsPage';
import ExamArchiveList from './components/admin/ExamArchiveList';
import { Teacher, User, Admin } from './types';
import { onAuthStateChange } from './services/authService';
import { db } from './firebase';
import { doc, getDoc } from 'firebase/firestore';
import { ToastProvider } from './contexts/ToastContext';

// Lazy load QuestionImageManagement
const QuestionImageManagement = lazy(() => import('./components/admin/QuestionImageManagement'));

// Check if running under /imtahan/ subpath
const isUnderImtahan = typeof window !== 'undefined' && window.location.pathname.startsWith('/imtahan');

const BackToPanelButton = () => {
  if (!isUnderImtahan) return null;
  return (
    <button
      onClick={() => window.location.href = '/'}
      style={{
        position: 'fixed',
        top: '12px',
        left: '12px',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 14px',
        background: 'rgba(255,255,255,0.15)',
        color: 'white',
        border: '1px solid rgba(255,255,255,0.3)',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: 500,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        transition: 'background 0.2s'
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.25)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
    >
      ← Geri
    </button>
  );
};

const AppContainer = styled.div`
  min-height: 100vh;
  padding: 20px;
  background: #1e3a8a;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
  color: white;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 8px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Subtitle = styled.p`
  font-size: 18px;
  opacity: 0.9;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const LoginWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80vh;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 80vh;
  color: white;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredStatus: 'teacher' | 'admin';
  user: User | null;
  isAuthenticated: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredStatus, 
  user, 
  isAuthenticated 
}) => {
  if (!isAuthenticated || !user) {
    return <Navigate to="/" replace />;
  }

  if (user.status !== requiredStatus) {
    // Eğer teacher gerekiyorsa ama admin ise, admin dashboard'a yönlendir
    if (requiredStatus === 'teacher' && user.status === 'admin') {
      return <Navigate to="/admin/dashboard" replace />;
    }
    // Eğer admin gerekiyorsa ama teacher ise, teacher dashboard'a yönlendir
    if (requiredStatus === 'admin' && user.status === 'teacher') {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};

// Main App Component
const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (firebaseUser) => {
      console.log('🔐 App: Auth state değişti - User:', firebaseUser ? firebaseUser.uid : 'null');
      
      if (firebaseUser) {
        console.log('✅ App: Kullanıcı giriş yapmış');
        setIsAuthenticated(true);
        
        try {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const currentUser: User = {
              id: firebaseUser.uid,
              name: userData.Name,
              status: userData.status,
              email: firebaseUser.email || '',
              fenn: userData.fenn,
              isActive: userData.isActive !== false
            };
            
            console.log('👤 App: Kullanıcı bilgileri yüklendi:', currentUser);
            setUser(currentUser);
          }
        } catch (error) {
          console.error('❌ App: Kullanıcı bilgileri yüklenirken hata:', error);
        }
      } else {
        console.log('❌ App: Kullanıcı çıkış yapmış');
        setIsAuthenticated(false);
        setUser(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleLogin = (userData: Teacher | Admin) => {
    console.log('🎓 App: Kullanıcı girişi yapıldı:', userData);
    const userObj: User = {
      id: userData.id,
      name: userData.name,
      status: userData.status as 'teacher' | 'admin',
      email: userData.email,
      fenn: 'fenn' in userData ? userData.fenn : undefined,
      isActive: 'isActive' in userData ? userData.isActive : true
    };
    setUser(userObj);
    setIsAuthenticated(true);
  };

  if (loading) {
    return (
      <AppContainer>
        <LoadingContainer>
          <LoadingSpinner />
          <div>Yüklənir...</div>
        </LoadingContainer>
      </AppContainer>
    );
  }

  return (
    <ToastProvider>
      <BackToPanelButton />
      <Router basename={window.location.pathname.startsWith('/imtahan') ? '/imtahan' : undefined}>
        <Routes>
        {/* Login Route */}
        <Route 
          path="/" 
          element={
            isAuthenticated && user ? (
              user.status === 'admin' ? (
                <Navigate to="/admin/dashboard" replace />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            ) : (
              <AppContainer>
                <Header>
                  <Title>İstedad Kursları İmtahan Qiymətləndirmə Sistemi</Title>
                  {/* <Subtitle>Müəllimlər üçün müasir qiymətləndirmə platforması</Subtitle> */}
                </Header>
                <LoginWrapper>
                  <AuthLogin onLogin={handleLogin} />
                </LoginWrapper>
              </AppContainer>
            )
          } 
        />

        {/* Teacher Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute requiredStatus="teacher" user={user} isAuthenticated={isAuthenticated}>
              {user && user.status === 'teacher' && (
                <TeacherDashboard 
                  teacher={{
                    id: user.id,
                    name: user.name,
                    fenn: user.fenn || '',
                    status: user.status,
                    email: user.email,
                    isActive: user.isActive
                  }} 
                />
              )}
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/yoxlama/:yoxlamaId/:questionNumber?"
          element={
            <ProtectedRoute requiredStatus="teacher" user={user} isAuthenticated={isAuthenticated}>
              {user && user.status === 'teacher' && (
                <YoxlamaPage
                  teacher={{
                    id: user.id,
                    name: user.name,
                    fenn: user.fenn || '',
                    status: user.status,
                    email: user.email,
                    isActive: user.isActive
                  }}
                />
              )}
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/buraxilis-yoxlama/:yoxlamaId/:questionNumber"
          element={
            <ProtectedRoute requiredStatus="teacher" user={user} isAuthenticated={isAuthenticated}>
              {user && user.status === 'teacher' && (
                <BuraxilisYoxlamaPage 
                  teacher={{
                    id: user.id,
                    name: user.name,
                    fenn: user.fenn || '',
                    status: user.status,
                    email: user.email,
                    isActive: user.isActive
                  }} 
                />
              )}
            </ProtectedRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute requiredStatus="admin" user={user} isAuthenticated={isAuthenticated}>
              {user && user.status === 'admin' && (
                <AdminDashboard adminName={user.name} adminId={user.id} />
              )}
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/teachers"
          element={
            <ProtectedRoute requiredStatus="admin" user={user} isAuthenticated={isAuthenticated}>
              <TeacherManagement />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/assignments"
          element={
            <ProtectedRoute requiredStatus="admin" user={user} isAuthenticated={isAuthenticated}>
              <AssignmentManagement />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/statistics"
          element={
            <ProtectedRoute requiredStatus="admin" user={user} isAuthenticated={isAuthenticated}>
              <StatisticsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/students"
          element={
            <ProtectedRoute requiredStatus="admin" user={user} isAuthenticated={isAuthenticated}>
              <StudentResultsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/exams"
          element={
            <ProtectedRoute requiredStatus="admin" user={user} isAuthenticated={isAuthenticated}>
              <ExamArchiveList />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/question-images"
          element={
            <ProtectedRoute requiredStatus="admin" user={user} isAuthenticated={isAuthenticated}>
              <Suspense fallback={
                <AppContainer>
                  <LoadingContainer>
                    <LoadingSpinner />
                    <div>Yüklənir...</div>
                  </LoadingContainer>
                </AppContainer>
              }>
                <QuestionImageManagement />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* 404 - Redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
    </ToastProvider>
  );
};

export default App;
